// web/src/features/tasks/MyTasks.jsx
import React from 'react'
import { Link } from 'react-router-dom'
import * as taskSvc from '@/services/firestore/taskService'
import useSession from '@/hooks/useSession'
import { compareTaskDueDate } from './task-due-order.mjs'
import './compliance-priority.css'
import { taskCountLabel } from './task-count-label.mjs'
import { auth } from '@/firebase'
import FollowupOutcome, {isFollowup} from './FollowupOutcome'

/* ---------------- helpers ---------------- */
const HEADER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 0,
  marginBottom: 22,
}

const STATUS = [
  'open',
  'awaiting_approval',
  'in_review',
  'changes_requested',
  'approved',
  'complete',
  'done',
  'closed',
]
const TYPES = [
  'approval',
  'review',
  'password_reset',
  'admin_request',
  'comment',
  'system',
  'draft',
]

const lc = (s) => (s || '').toString().trim().toLowerCase()
const normRole = (s) => lc(s).replace(/\s+/g, ' ')
function CreatedDate({value}) {
  if (!value) return '—'
  const date = new Date(value.seconds != null ? value.seconds * 1000 : value)
  if (Number.isNaN(date.getTime())) return '—'
  return <span className="compliance-task-created"><span>{date.toLocaleDateString('en-US', { timeZone: 'America/New_York' })}</span><span>{date.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}</span></span>
}
const isResolvedTask = t => !!t.completedAt || ['complete','completed','closed','done','cancelled','canceled','resolved'].includes(lc(t.status))
const showsResolved = status => ['all','resolved','complete','completed','closed','done','cancelled','canceled','approved_or_complete'].includes(lc(status))
const isOverdue = (t) =>
  t.dueAt &&
  new Date(t.dueAt.seconds ? t.dueAt.seconds * 1000 : t.dueAt) < new Date() &&
  !isResolvedTask(t)
const isDueSoon = (t, hrs = 72) => {
  if (!t.dueAt || isResolvedTask(t)) return false
  const d =
    new Date(
      t.dueAt.seconds ? t.dueAt.seconds * 1000 : t.dueAt
    ) - new Date()
  return d > 0 && d <= hrs * 3600 * 1000
}

/* dev fallback (keeps previous behavior when service funcs are missing) */
function readTasksLS() {
  try {
    return JSON.parse(localStorage.getItem('dev_tasks') || '[]')
  } catch {
    return []
  }
}
function listTasksDev() {
  try {
    // eslint-disable-next-line no-undef
    const mod = require('../../lib/devTasks')
    if (mod?.listTasks) return mod.listTasks()
  } catch {}
  return readTasksLS()
}
function syncFromCafsDev() {
  try {
    // eslint-disable-next-line no-undef
    const mod = require('../../lib/devTasks')
    if (mod?.syncFromCafs) mod.syncFromCafs()
  } catch {}
}

/* try multiple names so we don't break builds if your service changes */
let complianceTaskCache = { at: 0, rows: [] }
async function fetchAllTasks() {
  const rows = await fetchExistingTasks()
  if (import.meta.env.VITE_COMPLIANCE_ENABLED === 'false') return rows
  if (Date.now() - complianceTaskCache.at > 15000 || complianceTaskCache.user !== auth.currentUser?.uid) {
    const { api } = await import('../compliance/api')
    const tasks = await api('tasks')
    complianceTaskCache = { at: Date.now(), user: auth.currentUser?.uid, rows: tasks.map(t => ({ ...t, dueAt: t.dueAt || (t.dueDate ? `${t.dueDate}T23:59:59` : null), note: ['requirementReview','paymentRequirement'].includes(t.kind) ? t.note : t.communityName })) }
  }
  return [...rows, ...complianceTaskCache.rows]
}

async function fetchExistingTasks() {
  const fnsInOrder = [
    'listAllVisibleTasks',
    'listTasks',
    'listOpenTasks',
    'listMyTasks',
  ]
  for (const name of fnsInOrder) {
    const fn = taskSvc?.[name]
    if (typeof fn === 'function') {
      try {
        const rows = await fn()
        if (Array.isArray(rows)) return rows
      } catch (_) {}
    }
  }

  const buckets = []
  for (const name of [
    'listMyOpenTasks',
    'listAwaitingMyApprovalTasks',
    'listAdminOpenTasks',
  ]) {
    const fn = taskSvc?.[name]
    if (typeof fn === 'function') {
      try {
        const part = await fn()
        if (Array.isArray(part)) buckets.push(...part)
      } catch {}
    }
  }
  if (buckets.length) {
    const seen = new Set()
    return buckets.filter((t) => {
      const k =
        t.id ||
        `${t.cafId || ''}|${t.title || ''}|${t.createdAt || ''}`
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
  }

  return listTasksDev()
}

/* ---------------- component ---------------- */
export default function MyTasks() {
  const [raw, setRaw] = React.useState([])
  const [q, setQ] = React.useState(() => new URLSearchParams(location.search).get('community') || '')
  // Default to unresolved tasks
  const [status, setStatus] = React.useState('unresolved')
  const [type, setType] = React.useState(() => new URLSearchParams(location.search).get('type') === 'compliance' ? 'compliance' : 'All')
  const [scope, setScope] = React.useState(() => new URLSearchParams(location.search).get('scope') === 'all' ? 'all' : 'assigned') // assigned | created | all
  const [loading, setLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState('')
  const [refreshingTaskId, setRefreshingTaskId] = React.useState('')

  const sess = useSession()
  const myEmail = lc(sess?.email)

  const myCommunities = React.useMemo(
    () => {
      if (sess?.allCommunities === true) return new Set()
      return new Set(
        [...(Array.isArray(sess?.communities) ? sess.communities : []),
          ...(Array.isArray(sess?.communityCodes) ? sess.communityCodes : [])].map(lc)
      )
    },
    [sess]
  )

  const isAdminOrSysconfig = React.useMemo(() => {
    const lvl = lc(sess?.adminLevel)
    if (lvl === 'admin' || lvl === 'sysconfig') return true
    const roles = (Array.isArray(sess?.roles) ? sess?.roles : []).map(lc)
    return roles.includes('admin') || roles.includes('sysconfig')
  }, [sess])

  const myRoleLabels = React.useMemo(() => {
    const labels = []
    if (sess?.title) labels.push(sess.title)
    if (Array.isArray(sess?.roles)) labels.push(...sess.roles)
    return new Set(labels.map(normRole).filter(Boolean))
  }, [sess])

  React.useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        syncFromCafsDev()
        const rows = await fetchAllTasks()
        if (!alive) return
        setRaw(Array.isArray(rows) ? rows : [])
        setLoadError('')
      } catch (error) {
        if (alive) setLoadError(error.message)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    const i = setInterval(() => {
      fetchAllTasks().then((r) =>
        setRaw(Array.isArray(r) ? r : [])
      ).catch(error => alive && setLoadError(error.message))
    }, 2000)
    return () => {
      alive = false
      clearInterval(i)
    }
  }, [])

  const items = React.useMemo(() => {
    const term = q.trim().toLowerCase()

    return (raw || [])
      .filter((t) => {
        // -------- community scope for non-admins --------
        if (!isAdminOrSysconfig && sess?.allCommunities !== true) {
          const codeLc = lc(t.communityCode || t.community || '')
          const nameLc = lc(t.communityName || '')
          const hasCommunity = codeLc || nameLc

          if (
            hasCommunity &&
            !myCommunities.has(codeLc) &&
            !myCommunities.has(nameLc) &&
            !myCommunities.has(lc(t.communityId || ''))
          ) {
            return false
          }
        }

        // figure out relationships (including implicit approval + admin pw reset)
        const assignedByEmail = lc(t.assigneeEmail || '') === myEmail || [...(t.assigneeEmails || []), ...(t.backupEmails || [])].map(lc).includes(myEmail)

        const typeLc = lc(t.type)
        const isApproval = typeLc === 'approval'
        const isPasswordReset = typeLc === 'password_reset'

        const requiredRoleKeyNorm = normRole(
          t.requiredRoleKey || t.requiredRole || ''
        )

        // admins should "own" any password_reset tasks (audience ['admin'])
        const audienceHasAdmin = Array.isArray(t.audience)
          ? t.audience.some((a) => lc(a) === 'admin')
          : false

        const implicitAssigned =
          // approval routing: by role key
          (isApproval &&
            requiredRoleKeyNorm &&
            myRoleLabels.has(requiredRoleKeyNorm) &&
            (!t.assigneeEmail || lc(t.assigneeEmail) === '')) ||
          // password reset routing: any admin/sysconfig sees them as assigned
          (isPasswordReset && isAdminOrSysconfig && audienceHasAdmin)

        const assigned = assignedByEmail || implicitAssigned

        const creator =
          lc(t.createdByEmail || t.createdBy || '') === myEmail

        // scope filter
        const scopeOk =
          scope === 'all'
            ? true
            : scope === 'assigned'
            ? assigned
            : scope === 'created'
            ? creator
            : true

        // -------- status/type filters with Resolved vs Unresolved --------
        const s = lc(t.status)
        const ty = lc(t.type)
        const statusKey = lc(status)

        const isResolved = isResolvedTask(t)
        const isUnresolved = !isResolved

        let statusOk = true
        if (statusKey === 'unresolved' || statusKey === '' || !statusKey) {
          // default: only unresolved tasks
          statusOk = isUnresolved
        } else if (statusKey === 'resolved') {
          statusOk = isResolved
        } else if (statusKey === 'approved_or_complete') {
          statusOk = s === 'approved' || s === 'complete'
        } else if (statusKey === 'all') {
          // no status filter
          statusOk = true
        } else {
          statusOk = s === statusKey
        }

        const typeOk = type === 'All' || ty === lc(type)

        // search + due filters
        const hay = [
          t.title,
          t.number,
          t.cafNumber,
          t.cafId,
          t.communityName,
          t.communityCode,
          t.community,
          t.relatedLabel,
          t.note,
          t.type,
          t.status,
        ]
          .map((x) => (x || '').toString().toLowerCase())
          .join(' ')
        const baseMatch = !term || hay.includes(term)
        const dueOk =
          term === 'due:soon'
            ? isDueSoon(t)
            : term === 'due:over'
            ? isOverdue(t)
            : true

        return scopeOk && statusOk && typeOk && baseMatch && dueOk
      })
      .sort(compareTaskDueDate)
  }, [
    raw,
    q,
    status,
    type,
    scope,
    myEmail,
    myCommunities,
    myRoleLabels,
    isAdminOrSysconfig,
  ])
  const resolvedCount = React.useMemo(() => {
    const term = q.trim().toLowerCase()

    return (raw || [])
      .filter((t) => {
        // -------- community scope for non-admins --------
        if (!isAdminOrSysconfig && sess?.allCommunities !== true) {
          const codeLc = lc(t.communityCode || t.community || '')
          const nameLc = lc(t.communityName || '')
          const hasCommunity = codeLc || nameLc

          if (
            hasCommunity &&
            !myCommunities.has(codeLc) &&
            !myCommunities.has(nameLc) &&
            !myCommunities.has(lc(t.communityId || ''))
          ) {
            return false
          }
        }

        // -------- assignment / scope logic (same as items) --------
        const assignedByEmail = lc(t.assigneeEmail || '') === myEmail || [...(t.assigneeEmails || []), ...(t.backupEmails || [])].map(lc).includes(myEmail)

        const typeLc = lc(t.type)
        const isApproval = typeLc === 'approval'
        const isPasswordReset = typeLc === 'password_reset'

        const requiredRoleKeyNorm = normRole(
          t.requiredRoleKey || t.requiredRole || ''
        )

        const audienceHasAdmin = Array.isArray(t.audience)
          ? t.audience.some((a) => lc(a) === 'admin')
          : false

        const implicitAssigned =
          // approval routing: by role key
          (isApproval &&
            requiredRoleKeyNorm &&
            myRoleLabels.has(requiredRoleKeyNorm) &&
            (!t.assigneeEmail || lc(t.assigneeEmail) === '')) ||
          // password reset routing: any admin/sysconfig sees them as assigned
          (isPasswordReset && isAdminOrSysconfig && audienceHasAdmin)

        const assigned = assignedByEmail || implicitAssigned

        const creator =
          lc(t.createdByEmail || t.createdBy || '') === myEmail

        const scopeOk =
          scope === 'all'
            ? true
            : scope === 'assigned'
            ? assigned
            : scope === 'created'
            ? creator
            : true

        if (!scopeOk) return false

        // -------- status: only RESOLVED items --------
        const s = lc(t.status)
        const ty = lc(t.type)
        const isClosed =
          isResolvedTask(t)

        if (!isClosed) return false

        // -------- type filter (respect Type dropdown) --------
        const typeOk = type === 'All' || ty === lc(type)
        if (!typeOk) return false

        // -------- search + due filters (same as items) --------
        const hay = [
          t.title,
          t.number,
          t.cafNumber,
          t.cafId,
          t.communityName,
          t.communityCode,
          t.community,
          t.relatedLabel,
          t.note,
          t.type,
          t.status,
        ]
          .map((x) => (x || '').toString().toLowerCase())
          .join(' ')

        const baseMatch = !term || hay.includes(term)

        const dueOk =
          term === 'due:soon'
            ? isDueSoon(t)
            : term === 'due:over'
            ? isOverdue(t)
            : true

        return baseMatch && dueOk
      }).length
  }, [
    raw,
    q,
    type,
    scope,
    myEmail,
    myCommunities,
    myRoleLabels,
    isAdminOrSysconfig,
  ])
  
 const applyPreset = (name) => {
  switch (name) {
    case 'all':
      // Show all visible communities while respecting the resolved toggle.
      setScope('all')
      setStatus(current => showsResolved(current) ? 'all' : 'unresolved')
      setType('All')
      setQ('')
      break

    case 'assigned':
      // Unresolved items assigned to me (directly or implicitly)
      setScope('assigned')
      setStatus('unresolved')
      setType('All')
      setQ('')
      break

    case 'awaitingMine':
      // Approval items waiting on me
      setScope('assigned')
      setStatus('awaiting_approval')
      setType('approval')
      setQ('')
      break

    case 'needsReview':
      // CAF drafts sent back to me for changes
      setScope('assigned')
      setStatus('changes_requested')
      setType('draft')
      setQ('')
      break

    // case 'pwResets':
    //   // All unresolved password reset requests:
    //   //  - Admin/Sysconfig: all unresolved resets
    //   //  - Others: unresolved resets I created
    //   setScope(isAdminOrSysconfig ? 'all' : 'created')
    //   setStatus('unresolved')
    //   setType('password_reset')
    //   setQ('')
    //   break

    case 'drafts':
      // My unresolved drafts
      setScope('created')
      setStatus('open')
      setType('draft')
      setQ('')
      break

    case 'submitted':
      // CAFs I've submitted that are awaiting approval
      setScope('created')
      setStatus('awaiting_approval')
      setType('approval')
      setQ('')
      break

    case 'inReview':
      // All CAFs currently in approval, regardless of who created them
      setScope('all')
      setStatus('awaiting_approval')
      setType('approval')
      setQ('')
      break

    case 'approved':
      // Approved / Completion items I created that still need follow-up
      // (e.g. approved CAFs where completion work is outstanding)
      setScope('created')
      setStatus('approved_or_complete')
      setType('approval')
      setQ('')
      break

    case 'resolved':
      // All resolved tasks (complete / done / closed), any scope
      setScope('all')
      setStatus('resolved')
      setType('All')
      setQ('')
      break

    default:
      break
  }
}

  // helper: make the Task cell link to CAF (or relatedUrl) when available
  const renderTaskTitle = (t) => {
    const cafKey = t.cafNumber || t.cafId || ''

    const inner = (
      <div style={{ display: 'flex', flexDirection: 'column' }}>

        <strong className="truncate">
          {t.title || 'Task'}
        </strong>
        {t.note && (
          <small className="muted truncate" data-tooltip={t.note} tabIndex={0}>
            {t.note}
          </small>
        )}
      </div>
    )

    if (cafKey) {
      return (
        <Link
          to={`/dev/caf/${encodeURIComponent(cafKey)}`}
          className="truncate task-title-link"
        >
          {inner}
        </Link>
      )
    }

    if (t.relatedUrl) {
      return (
        <a
          href={t.relatedUrl}
          target="_blank"
          rel="noreferrer"
          className="truncate task-title-link"
        >
          {inner}
        </a>
      )
    }

    return inner
  }

  const getAssignedText = (t) => {
    const typeLc = lc(t?.type || '')
    const raw =
      t.assigneeName ||
      t.assigneeEmail ||
      (typeLc !== 'approval' ? (t.createdByName || t.createdByEmail || '') : '') ||
      t.requiredRole ||
      ''
    return raw.replace(/^Role:\s*/i, '') || '—'
  }

  const pillCellStyle = {
    textAlign: 'center',
    verticalAlign: 'middle',
  }
  const centeredHeader = {
    textAlign: 'center',
  }

  // mark-complete handler for password reset tasks
  const handleComplete = async (t) => {
    try {
      if (!t.id) return
      if (lc(t.type) === 'property_setup') {
        if (t.managedCityMatch || isFollowup(t)) return
        if (!isAdminOrSysconfig && lc(t.assigneeEmail) !== myEmail) return
        await taskSvc.completeTaskFS(t.id, { completedByEmail: myEmail })
      } else if (lc(t.type) === 'password_reset') {
        await taskSvc.completePasswordResetTask(t.id)
      } else return

      // optimistic local update
      setRaw((prev) =>
        (prev || []).map((row) =>
          row.id === t.id
            ? {
                ...row,
                status: 'complete',
                completedAt: { seconds: Math.floor(Date.now() / 1000) },
              }
            : row
        )
      )
    } catch (e) {
      console.error('Failed to complete task', e)
    }
  }

  const handleRefreshAssignment = async (t) => {
    try {
      if (!t?.id || !isAdminOrSysconfig) return
      setRefreshingTaskId(t.id)
      await taskSvc.refreshTaskAssignmentFS(t.id)
      const rows = await fetchAllTasks()
      setRaw(Array.isArray(rows) ? rows : [])
    } catch (e) {
      console.error('Failed to refresh task assignment', e)
    } finally {
      setRefreshingTaskId('')
    }
  }

  return (
    <div>
      <h2
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 14,
          marginBottom: 12,
        }}
      >
        <span>My Tasks</span>
      </h2>

      {/* Presets */}
      <div
  className="card"
  style={{
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  }}
>
  <label className="task-resolved-toggle"><input type="checkbox" role="switch" checked={showsResolved(status)} onChange={e=>setStatus(e.target.checked?'all':'unresolved')}/>Show resolved tasks</label>
  <strong>Presets:</strong>
  <button className="preset-btn" onClick={() => applyPreset('all')}>
    All
  </button>
  <button className="preset-btn" onClick={() => applyPreset('assigned')}>
    Assigned to Me
  </button>
  <button className="preset-btn" onClick={() => applyPreset('awaitingMine')}>
    Awaiting My Approval
  </button>
  <button className="preset-btn" onClick={() => applyPreset('needsReview')}>
    Needs My Changes
  </button>
  {/* Password Resets preset commented out */}
  {/* <button className="preset-btn" onClick={() => applyPreset('pwResets')}>
    Password Resets
  </button> */}
  <span style={{ marginLeft: 1, opacity: 0.7 }}>|</span>
  <button className="preset-btn" onClick={() => applyPreset('drafts')}>
    My Drafts
  </button>
  <button className="preset-btn" onClick={() => applyPreset('submitted')}>
    Submitted
  </button>
  <button className="preset-btn" onClick={() => applyPreset('inReview')}>
    In Review
  </button>
  <button className="preset-btn" onClick={() => applyPreset('approved')}>
    Approved / Completion
  </button>
  <button className="preset-btn" onClick={() => applyPreset('resolved')}>
    Resolved
  </button>
</div>

      {/* Filters */}
      <div
        className="mobile-stack-grid card"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 2fr auto',
          gap: 8,
          alignItems: 'end',
        }}
      >
        <div>
          <label>Scope</label>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
          >
            <option value="assigned">Assigned to me</option>
            <option value="created">Created by me</option>
            <option value="all">All visible</option>
          </select>
        </div>
        <div>
          <label>Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="unresolved">Unresolved</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
            {STATUS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option>All</option>
            <option value="compliance">Compliance</option>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Search</label>
          <input
            placeholder="Search community (e.g. 210), task, CAF #, or note"
            aria-label="Search tasks by community, title, CAF number, or note"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div>
          <button
            onClick={() => {
              setScope('assigned')
              setStatus('unresolved')
              setType('All')
              setQ('')
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Task table */}
<div className="card">
        <h3
          style={{
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span>
            {taskCountLabel(status)} (
            {items.length})
          </span>

          {resolvedCount > 0 && ['all', 'unresolved', ''].includes(lc(status)) && (
            <span
              className="badge"
              style={{ fontSize: 11, textTransform: 'none' }}
            >
              Resolved ({resolvedCount})
            </span>
          )}
        </h3>
        <p className="muted task-color-legend">Red: overdue · Amber: due within 3 days · Neutral: other open tasks · Muted: resolved</p>
        <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table my-tasks-table">
          <colgroup>
            {/* Task gets the most room */}
            <col style={{ width: '23%' }} />  {/* Task */}
            <col style={{ width: '8%' }} />  {/* Status */}
            <col style={{ width: '9%' }} />  {/* Type */}
            <col style={{ width: '14%' }} />  {/* Related */}
            <col style={{ width: '12%' }} />  {/* Assigned */}
            <col style={{ width: '13%' }} />  {/* Due */}
            <col style={{ width: '12%' }} />  {/* Created */}
            <col style={{ width: '9%' }} />  {/* Actions */}
          </colgroup>
          <thead>
            <tr>
              <th>Task</th>
              <th style={centeredHeader} className="nowrap">Status</th>
              <th style={centeredHeader} className="nowrap">Type</th>
              <th className="nowrap">Related</th>
              <th className="nowrap">Assigned</th>
              <th className="nowrap">Due date</th>
              <th className="nowrap">Created</th>
              <th style={centeredHeader} className="nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadError && <tr><td colSpan={8} role="alert">Task refresh failed: {loadError}</td></tr>}
            {loading && (
              <tr>
                <td colSpan={8}>
                  <div className="muted">Loading…</div>
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} style={{ opacity: 0.7 }}>
                  No tasks match your filters.
                </td>
              </tr>
            )}
            {!loading &&
              items.map((t) => {
                const statusLc = lc(t.status || 'open')
                const typeLc = lc(t.type || '')

                const canCompletePwReset =
                  isAdminOrSysconfig &&
                  typeLc === 'password_reset' &&
                  statusLc !== 'complete' &&
                  statusLc !== 'closed' &&
                  statusLc !== 'done'

                return (
                  <tr key={t.id || t.number} className={`${t.type === 'compliance' ? 'compliance-task-record ' : ''}task-tone-${isResolvedTask(t)?'resolved':isOverdue(t)?'overdue':isDueSoon(t)?'soon':'open'}`}>
                    {/* NOTE: no "truncate" here so we can see the full text */}
                    <td className="task-multiline">
                      {renderTaskTitle(t)}
                      {t.kind === 'requirementReview' && <details><summary>Outstanding requirements</summary><p style={{whiteSpace:'pre-wrap'}}>{t.instructions || 'All requirements reviewed.'}</p><p>Regional backup: {t.backupNames?.join(', ') || 'None assigned'}</p><a href={t.relatedUrl}>Review requirements</a></details>}
                      {t.type === 'property_setup' && <details><summary>Setup instructions</summary><p style={{whiteSpace:'pre-wrap'}}>{t.instructions}</p>{t.referenceLinks?.filter(link=>/^https:\/\//.test(link.url)).map(link=><p key={link.url}><a href={link.url} target="_blank" rel="noreferrer">{link.label}</a></p>)}</details>}
                    </td>

                    {/* Status – CENTERED badge */}
                    <td style={pillCellStyle} className="nowrap">
                      <span
                        className="badge"
                        style={{
                          display: 'inline-block',
                          padding: '2px 12px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isOverdue(t) ? 'overdue' : t.status || 'open'}
                      </span>
                    </td>

                    {/* Type – CENTERED badge */}
                    <td style={pillCellStyle} className="nowrap">
                      <span
                        className="badge"
                        style={{
                          display: 'inline-block',
                          padding: '2px 12px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t.type || '—'}
                      </span>
                    </td>

                    <td className="nowrap">
                      {t.cafNumber ||
                        t.cafId ||
                        t.relatedLabel ||
                        '—'}
                    </td>

                    <td className="task-assigned-cell">
                      {t.type === 'compliance' && t.assignees?.length ? <span className="compliance-assignees">{t.assignees.map(person => <span key={person.email} data-tooltip={person.email} tabIndex={0}>{person.name}</span>)}</span> : getAssignedText(t)}
                    </td>

                    <td className="task-created-cell"><CreatedDate value={t.dueAt}/></td>
                    <td className="task-created-cell"><CreatedDate value={t.createdAt}/></td>

                    <td style={{ textAlign: 'center' }} className="nowrap">
                      {isFollowup(t) ? <FollowupOutcome task={t} email={myEmail} onSaved={async()=>setRaw(await fetchAllTasks())}/> : (canCompletePwReset || (typeLc === 'property_setup' && !t.managedCityMatch && statusLc === 'open' && (isAdminOrSysconfig || lc(t.assigneeEmail) === myEmail))) ? (
                        <button
                          type="button"
                          className="preset-btn"
                          onClick={() => handleComplete(t)}
                          style={{ padding: '2px 8px', fontSize: 12 }}
                        >
                          Mark Complete
                        </button>
                      ) : (
                        t.kind === 'requirementReview' && statusLc === 'open' ? 'Closes after review' : t.managedCityMatch && statusLc === 'open' ? 'Closes when linked' : '—'
                      )}
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </table></div>
      </div>

      <p style={{ marginTop: 8 }} className="muted">
        {/* Presets adjust the filters only. Community scoping is enforced
        for non-admins. Role-based approval tasks appear under
        “Awaiting My Approval” even when no individual assignee is set.*/}
      </p>
    </div>
  )
}
